/**
 * ScopeContext — exposes the resolved Institution/Chapter/Branch context for
 * the current route. Routes under /c/:chapterSlug and /c/:chapterSlug/b/:branchSlug
 * read params and call /api/scope/resolve once per scope change.
 */

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useParams } from 'react-router';

export interface ResolvedInstitution {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  defaultChapterSlug?: string;
}
export interface ResolvedChapter {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  batchYear?: number;
  landingPageId?: string;
}
export interface ResolvedBranch {
  id: string;
  slug: string;
  name: string;
  displayName?: string;
  kind: string;
  landingPageId?: string;
}

export interface ScopeState {
  institution?: ResolvedInstitution;
  chapter?: ResolvedChapter;
  branch?: ResolvedBranch;
  loading: boolean;
  error?: string;
}

const ScopeContext = createContext<ScopeState>({ loading: false });

interface ScopeProviderProps {
  children: ReactNode;
  /** Override the chapter slug (used by LandingRouter at `/`). */
  defaultChapterSlug?: string;
}

export function ScopeProvider({ children, defaultChapterSlug }: ScopeProviderProps) {
  const params = useParams();
  const chapterSlug = (params['chapterSlug'] as string | undefined) ?? defaultChapterSlug;
  const branchSlug = params['branchSlug'] as string | undefined;

  const [state, setState] = useState<ScopeState>({ loading: !!chapterSlug });

  useEffect(() => {
    let cancelled = false;
    const apiBase =
      (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ??
      'http://localhost:3001';
    const params = new URLSearchParams();
    params.set('institution', 'navarasam');
    if (chapterSlug) params.set('chapter', chapterSlug);
    if (branchSlug) params.set('branch', branchSlug);

    setState((s) => ({ ...s, loading: true, error: undefined }));
    fetch(`${apiBase}/api/scope/resolve?${params.toString()}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setState({
          institution: data.institution,
          chapter: data.chapter ?? undefined,
          branch: data.branch ?? undefined,
          loading: false,
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ loading: false, error: err.message });
      });
    return () => { cancelled = true; };
  }, [chapterSlug, branchSlug]);

  const value = useMemo(() => state, [state]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): ScopeState {
  return useContext(ScopeContext);
}
